import React, { useState, useEffect } from "react";
import {
  TextField,
  Box,
  Typography,
  InputAdornment,
  Grid,
  Paper,
  Divider,
} from "@mui/material";
import {
  Person,
  Class as ClassIcon,
  Numbers,
  Email,
  Phone,
  MonetizationOn,
  SupervisorAccount,
  Home as HostelIcon,
  AttachMoney as TotalIcon,
  Payment as UpiIcon,
} from "@mui/icons-material";
import axios from "../../axiosInstance";
import CustomButton from "../../Components/CustomButton";
import { message } from 'antd';

const StudentFeeForm = ({ onSuccess }) => {
  const [form, setForm] = useState({
    studentName: "",
    studentClass: "",
    rollNumber: "",
    parentName: "",
    parentEmail: "",
    parentPhone: "",
    feesAmount: "",
    hostelFees: "",
    totalFees: "",
  });

  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    const fees = parseFloat(form.feesAmount) || 0;
    const hostel = parseFloat(form.hostelFees) || 0;
    setForm((prev) => ({ ...prev, totalFees: (fees + hostel).toString() }));
  }, [form.feesAmount, form.hostelFees]);

  const generateUpiLink = () => {
    const upiId = "msola33052@ybl"; 
    const { totalFees } = form;
    if (!totalFees || parseFloat(totalFees) <= 0 || !upiId) return null;
    return `upi://pay?pa=${upiId}&pn=Shree Mangalam School&am=${totalFees}&cu=INR`;
  };

  const generateQrCodeUrl = () => {
    const upiLink = generateUpiLink();
    return upiLink ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(upiLink)}` : null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const qrCodeUrl = generateQrCodeUrl();
    if (!qrCodeUrl) {
      message.error("Please enter valid fee amounts");
      setLoading(false);
      return;
    }

    try {
      const submissionData = {
        ...form,
        qrCodeUrl: qrCodeUrl,
        upiLink: generateUpiLink()
      };

      const res = await axios.post("/api/v1/fees/initiate", submissionData);
      
      if (res.data.success) {
        message.success(res.data.message || "Payment initiated successfully!");
        setForm({
          studentName: "",
          studentClass: "",
          rollNumber: "",
          parentName: "",
          parentEmail: "",
          parentPhone: "",
          feesAmount: "",
          hostelFees: "",
          totalFees: "",
        });

        if (onSuccess) {
          onSuccess({
            ...res.data,
            qrCodeUrl: qrCodeUrl,
            feesAmount: form.totalFees
          });
        }
      } else {
        message.error(res.data.message || "Failed to initiate payment.");
      }
    } catch (err) {
      message.error(err.response?.data?.message || "An error occurred while initiating payment.");
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (field) => {
    const icons = {
      studentName: <Person sx={{ color: "#065A79" }} />,
      studentClass: <ClassIcon sx={{ color: "#065A79" }} />,
      rollNumber: <Numbers sx={{ color: "#065A79" }} />,
      parentName: <SupervisorAccount sx={{ color: "#065A79" }} />,
      parentEmail: <Email sx={{ color: "#065A79" }} />,
      parentPhone: <Phone sx={{ color: "#065A79" }} />,
      feesAmount: <MonetizationOn sx={{ color: "#065A79" }} />,
      hostelFees: <HostelIcon sx={{ color: "#065A79" }} />,
      totalFees: <TotalIcon sx={{ color: "#065A79" }} />,
    };
    return icons[field] || null;
  };

  const fields = [
    {
      name: "studentName",
      label: "Student Name",
      type: "text",
      gridSize: 6,
      placeholder: "Enter Student Name",
    },
    {
      name: "studentClass",
      label: "Student Class",
      type: "text",
      gridSize: 6,
      placeholder: "Enter Student Class",
    },
    {
      name: "rollNumber",
      label: "Roll Number",
      type: "number",
      gridSize: 6,
      placeholder: "Enter Roll Number",
    },
    {
      name: "parentName",
      label: "Parent Name",
      type: "text",
      gridSize: 6,
      placeholder: "Enter Parent Name",
    },
    {
      name: "parentEmail",
      label: "Parent Email",
      type: "email",
      gridSize: 6,
      placeholder: "Enter Parent Email",
    },
    {
      name: "parentPhone",
      label: "Parent Phone",
      type: "tel",
      gridSize: 6,
      placeholder: "Enter Parent Phone",
    },
    {
      name: "feesAmount",
      label: "Fees Amount",
      type: "number",
      gridSize: 4,
      placeholder: "Enter Fees Amount",
    },
    {
      name: "hostelFees",
      label: "Hostel Fees",
      type: "number",
      gridSize: 4,
      placeholder: "Enter Hostel Fees",
    },
    {
      name: "totalFees",
      label: "Total Fees",
      type: "number",
      gridSize: 4,
      disabled: true,
    },
  ];

  return (
    <Paper
      elevation={3}
      sx={{
        p: 4,
        mt: 4,
        borderRadius: 3,
        mx: "auto",
        bgcolor: "background.paper",
        maxWidth: 900,
      }}
    >
      <Box component="form" onSubmit={handleSubmit}>
        <Typography
          variant="h5"
          sx={{
            mb: 3,
            fontWeight: 600,
            color: "#AD1C86",
            textAlign: "center",
            letterSpacing: 1,
          }}
        >
          School Fee Payment
        </Typography>

        <Divider sx={{ mb: 4 }} />

        <Typography variant="h6" sx={{ mb: 2, color: "#AD1C86" }}>
          Student Information
        </Typography>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {fields.slice(0, 3).map((field) => (
            <Grid item xs={12} sm={field.gridSize} key={field.name}>
              <TextField
                fullWidth
                variant="outlined"
                name={field.name}
                label={field.label}
                value={form[field.name]}
                placeholder={field.placeholder}
                onChange={handleChange}
                type={field.type}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {getIcon(field.name)}
                    </InputAdornment>
                  ),
                }}
                
                InputLabelProps={{
                  sx: {
                    color: "#065A79",
                    "&.Mui-focused": {
                      color: "#AD1C86",
                      fontWeight: "bold",
                    },
                  },
                }}
                sx={{
                  "& input[type='number']": {
                    MozAppearance: "textfield",
                    "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button":
                      {
                        display: "none",
                      },
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#065A79",
                    },
                    "&:hover fieldset": {
                      borderColor: "#065A79",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#AD1C86",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderRadius: "10px",
                    },
                  },
                }}
              />
            </Grid>
          ))}
        </Grid>

        <Typography variant="h6" sx={{ mb: 2, color: "#AD1C86" }}>
          Parent Information
        </Typography>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {fields.slice(3, 6).map((field) => (
            <Grid item xs={12} sm={field.gridSize} key={field.name}>
              <TextField
                fullWidth
                variant="outlined"
                name={field.name}
                label={field.label}
                placeholder={field.placeholder}
                value={form[field.name]}
                onChange={handleChange}
                type={field.type}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {getIcon(field.name)}
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{
                  sx: {
                    color: "#065A79",
                    "&.Mui-focused": {
                      color: "#AD1C86",
                      fontWeight: "bold",
                    },
                  },
                }}
                sx={{
                  "& input[type='number']": {
                    MozAppearance: "textfield",
                    "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button":
                      {
                        display: "none",
                      },
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#065A79",
                    },
                    "&:hover fieldset": {
                      borderColor: "#065A79",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#AD1C86",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderRadius: "10px",
                    },
                  },
                }}
              />
            </Grid>
          ))}
        </Grid>

        <Typography variant="h6" sx={{ mb: 2, color: "#AD1C86" }}>
          Fee Details
        </Typography>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {fields.slice(6).map((field) => (
            <Grid item xs={12} sm={field.gridSize} key={field.name}>
              <TextField
                fullWidth
                variant="outlined"
                name={field.name}
                label={field.label}
                placeholder={field.placeholder}
                value={form[field.name]}
                onChange={handleChange}
                type={field.type}
                disabled={field.disabled}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {getIcon(field.name)}
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{
                  sx: {
                    color: "#065A79",
                    "&.Mui-focused": {
                      color: "#AD1C86",
                      fontWeight: "bold",
                    },
                  },
                }}
                sx={{
                  "& input[type='number']": {
                    MozAppearance: "textfield",
                    "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button":
                      {
                        display: "none",
                      },
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#065A79",
                    },
                    "&:hover fieldset": {
                      borderColor: "#065A79",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#AD1C86",
                    },
                    "&.Mui-disabled": { backgroundColor: "action.hover" },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderRadius: "10px",
                    },
                  },
                }}
              />
            </Grid>
          ))}
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <CustomButton
            ButtonName={loading ? "Processing..." : "Proceed To Pay"}
            type="submit"
            disabled={loading}
            handleSubmit={handleSubmit}
          />
        </Box>
      </Box>
    </Paper>
  );
};

export default StudentFeeForm;
